/**
 * DepotPiece — RP51 / RP52 (revue produit 2026-09, R4 ; D22, D24).
 *
 * Zone de dépôt d'une pièce par lot et avec indice :
 *  - partenaire (rôle projet « consultant ») : la pièce est visible tout de suite
 *    par l'équipe de maîtrise d'œuvre ; l'architecte décide de sa diffusion (D24) ;
 *  - entreprise : document d'exécution « À viser » (un visa est créé, le viseur est
 *    désigné : partenaire du lot, sinon l'architecte ; délai de 15 jours).
 *
 * Endpoint : POST /api/chantiers/{cid}/depots (multipart).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { CloudArrowUp, Spinner } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { NATURES_PIECE, DELAI_VISA_JOURS } from './visasLibelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SANS_LOT = '__aucun__';

export default function DepotPiece({ chantierId, roleProjet, onDeposited, compact = false }) {
  const estEntreprise = roleProjet === 'entreprise';
  const [lots, setLots] = useState([]);
  const [file, setFile] = useState(null);
  const [lot, setLot] = useState(SANS_LOT);
  const [indice, setIndice] = useState('A');
  const [nature, setNature] = useState(estEntreprise ? 'plan' : 'note_calcul');
  const [titre, setTitre] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    let annule = false;
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true })
      .then(({ data }) => { if (!annule) setLots(Array.isArray(data) ? data : []); })
      .catch(() => { if (!annule) setLots([]); });
    return () => { annule = true; };
  }, [chantierId]);

  useEffect(() => {
    // une entreprise titulaire d'un seul lot n'a pas à le choisir
    if (estEntreprise && lots.length === 1) setLot(lots[0].id);
  }, [estEntreprise, lots]);

  const choisirFichier = (f) => {
    if (!f) return;
    setFile(f);
    if (!titre) setTitre(f.name.replace(/\.[^.]+$/, ''));
  };

  const deposer = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choisissez un fichier'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('lot', lot === SANS_LOT ? '' : lot);
    fd.append('indice', indice || 'A');
    fd.append('nature', nature);
    fd.append('titre', titre || file.name);
    fd.append('doc_type', nature === 'cctp' ? 'cctp' : 'plans');
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/depots`, fd, { withCredentials: true });
      if (data.visa) {
        toast.success(`Document déposé : à viser par ${data.visa.viseur_nom || "l'architecte"} avant le ${new Date(data.visa.date_limite).toLocaleDateString('fr-FR')}`);
      } else {
        toast.success("Document déposé : visible par l'équipe de maîtrise d'œuvre");
      }
      setFile(null); setTitre(''); setIndice('A');
      if (onDeposited) onDeposited(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dépôt impossible');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form
      onSubmit={deposer}
      className={`border border-dashed ${dragOver ? 'border-[#C4773B] bg-[#C4773B]/5' : 'border-[#E0E0DB] bg-white'} p-4 ${compact ? '' : 'mb-6'}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); choisirFichier(e.dataTransfer.files?.[0]); }}
      data-testid="depot-piece"
    >
      <div className="flex items-center gap-2 mb-3">
        <CloudArrowUp size={20} className="text-[#C4773B]" />
        <p className="text-sm font-semibold text-[#2C2C2C]">
          {estEntreprise ? "Déposer un document d'exécution à viser" : 'Déposer une pièce'}
        </p>
      </div>
      <p className="text-xs text-[#6B6B6B] mb-3">
        {estEntreprise
          ? `Le viseur (partenaire du lot ou architecte) rend son avis sous ${DELAI_VISA_JOURS} jours : VSO, VAO ou Refusé.`
          : "Votre pièce est visible tout de suite par l'équipe de maîtrise d'œuvre. L'architecte décide de sa diffusion aux entreprises et au client."}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <div className="lg:col-span-2">
          <Label className="text-xs">Fichier</Label>
          <Input type="file" onChange={(e) => choisirFichier(e.target.files?.[0])} className="rounded-none" data-testid="depot-fichier" />
        </div>
        <div>
          <Label className="text-xs">Lot</Label>
          <Select value={lot} onValueChange={setLot}>
            <SelectTrigger className="rounded-none" data-testid="depot-lot"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SANS_LOT}>Sans lot</SelectItem>
              {lots.map((l) => <SelectItem key={l.id} value={l.id}>{l.code} — {l.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Nature</Label>
          <Select value={nature} onValueChange={setNature}>
            <SelectTrigger className="rounded-none" data-testid="depot-nature"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(NATURES_PIECE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Indice</Label>
          <Input value={indice} maxLength={4} onChange={(e) => setIndice(e.target.value.toUpperCase())} className="rounded-none" data-testid="depot-indice" />
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <Label className="text-xs">Titre</Label>
          <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex. : Plan de réseau EU, R+1" className="rounded-none" data-testid="depot-titre" />
        </div>
        <Button type="submit" disabled={envoi || !file} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none gap-2" data-testid="depot-envoyer">
          {envoi ? <Spinner size={16} className="animate-spin" /> : <CloudArrowUp size={16} />}
          Déposer
        </Button>
      </div>
    </form>
  );
}
